import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { FeedbackService, FlyoutService } from '@app/core/services';
import { PurchaseCardImportSetupModel } from '@app/models/system-settings/purchase-card-import-setup.model';
import { FormComponent } from '@app/shared/forms/components/form.component';
import { PurchaseCardImportSetupService } from '../../services/purchase-card-import-setup.service';

@Component({
	selector: 'purchase-card-import-setup',
	templateUrl: './import-setup.component.html',
	standalone: true,
	imports: [FormComponent],
})
export class PurchaseCardImportSetupComponent implements OnInit {
	@ViewChild(FormComponent) formComponent: FormComponent;

	setupEntity: PurchaseCardImportSetupModel = {
		Columns: [],
	};

	form = new UntypedFormGroup({});
	options: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			readOnly: false,
			flyout: true,
			selects: {
				fileTypes: [],
				paymentTypes: [],
				columnMappings: [],
			},
		},
	};

	fields: FormlyFieldConfig[] = [
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimpleTitle],
			templateOptions: {
				label: 'Initial File Setup',
				section: 'details',
			},
			fieldGroup: [
				{
					key: 'FileTypeId',
					type: FORMLY_FIELD_TYPES.select,
					templateOptions: {
						label: 'File Type',
						required: true,
					},
					expressionProperties: {
						'templateOptions.options': 'formState.selects.fileTypes',
					},
				},
				{
					key: 'PaymentTypeId',
					type: FORMLY_FIELD_TYPES.select,
					templateOptions: {
						label: 'Default Invoice Payment By',
						required: true,
					},
					expressionProperties: {
						'templateOptions.options': 'formState.selects.paymentTypes',
					},
				},
				{
					key: 'IgnoreHeader',
					type: FORMLY_FIELD_TYPES.checkbox,
					templateOptions: {
						label: 'Ignore Column Header',
						appearance: 'standard',
						required: true,
					},
				},
				{
					key: 'ItemQuantity',
					type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
					defaultValue: 1,
					templateOptions: {
						label: 'Item Quantity',
						required: true,
						min: 1,
						max: 2147483647,
					},
				},
			],
		},
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimpleTitle],
			templateOptions: {
				label: 'Data Mapping Column Numbers',
				section: 'details',
			},
			fieldGroup: [
				{
					key: 'Columns',
					type: FORMLY_FIELD_TYPES.repeat,
					templateOptions: {
						addText: 'Add Column Mapping',
						removed: () => this.updateColumnMappingOptions(),
					},
					fieldArray: {
						fieldGroup: [
							{
								className: 'col-xs-4',
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								key: 'ColumnNumber',
								templateOptions: {
									label: 'Column #',
									required: true,
									min: 1,
									max: 2147483647,
								},
							},
							{
								className: 'col-xs-6',
								type: FORMLY_FIELD_TYPES.select,
								key: 'ColumnMappingId',
								templateOptions: {
									label: 'Mapping',
									required: true,
									change: (): void => this.updateColumnMappingOptions(),
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.columnMappings',
								},
							},
						],
					},
				},
			],
		},
	];

	constructor(
		private readonly feedbackService: FeedbackService,
		private readonly flyoutService: FlyoutService,
		private readonly entityService: PurchaseCardImportSetupService
	) {}

	ngOnInit(): void {
		this.entityService.getLookups().subscribe((result) => {
			this.options.formState.selects.fileTypes = result.FileTypes.map((item) => ({
				value: item.Id,
				label: `${item.Description}`,
			}));

			this.options.formState.selects.paymentTypes = result.PaymentTypes.map((item) => ({
				value: item.Id,
				label: `${item.Description}`,
			}));

			this.options.formState.selects.columnMappings = result.ColumnMappings.map((item) => ({
				value: item.Id,
				label: `${item.Description}`,
			}));
		});
	}

	edit(paymentCardId: number): void {
		this.entityService.getSetupByPaymentCardId(paymentCardId).subscribe((result: PurchaseCardImportSetupModel) => {
			this.setupEntity.PaymentCardId = paymentCardId;
			if (result) {
				this.setupEntity = result;
			}
			this.updateColumnMappingOptions();
			this.formComponent.edit('details');
		});
	}

	onSetupSubmit = (model: PurchaseCardImportSetupModel): void => {
		this.feedbackService.clear(undefined, 'error', 'flyout');

		this.entityService.createOrUpdate(model).subscribe((data) => {
			if (data.HasErrors) {
				data.ValidationEntries.Errors.forEach((x) => this.feedbackService.error(x, 'flyout'));
			} else {
				this.feedbackService.success('updated', 'Setup');
				this.setupEntity = data.Entity;
				this.flyoutService.close();
			}
		});
	};

	private updateColumnMappingOptions(): void {
		let columns = [];
		if (this.form.getRawValue().Columns) {
			columns = this.form.getRawValue().Columns;
		} else {
			columns = this.setupEntity.Columns;
		}
		const selectedColumnMappingIds = columns.map((x) => x.ColumnMappingId);
		this.options.formState.selects.columnMappings = this.options.formState.selects.columnMappings.map((x) => {
			if (selectedColumnMappingIds.includes(x.value)) {
				return { ...x, disabled: 'true' };
			} else {
				return { ...x, disabled: 'false' };
			}
		});
	}
}
